import { IAdvertiser } from "api/types/_advertiser";
import { Option } from "api/types/_public";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownAdvertiser from "components/Common/DropdownAdvertiser";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import { useRole } from "components/Hooks/UserHooks";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { ARR_INDEX_TEXT_DAY_OF_WEEK } from "helpers/constans";
import { formatNumberWithCommas } from "helpers/format";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { getAllAdvertisers, advertiserAgencyReport as onGetReportAdvertiserAgency } from "../../../store/thunks";
import ChartLine from "./ChartLine";
import CopyWrapper from "components/Common/CopyWrapper";


const SORT_BY_DEFAULT = 'date';
const ORDER_BY_DEFAULT = 'DESC';

registerLocale('en', en);
registerLocale('ko', ko);

const AdvertiserAgencyReport = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();
    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        // advertiser_id: withDefault(StringParam, ''),
        start_date: withDefault(StringParam, `${moment(new Date(new Date().setDate(new Date().getDate() - 30))).format("Y-MM-DD")}`),
        end_date: withDefault(StringParam, moment(new Date()).format("Y-MM-DD")),
    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Report;

    const ReportProperties = createSelector(
        selectLayoutState,
        (state) => ({
            advertiserAgencies: state.advertiserAgencies,
            isAdvertiserAgencyReportSuccess: state.isAdvertiserAgencyReportSuccess,
            isAdvertiserAgencyReportLoading: state.isAdvertiserAgencyReportLoading,
            error: state.error,
        })
    );

    const { advertiserAgencies, isAdvertiserAgencyReportLoading } = useSelector(ReportProperties);

    const [dateSearch, setDateSearch] = useState<any[]>([query?.start_date ? moment(query?.start_date, 'Y-MM-DD').toDate() : null, query?.end_date ? moment(query?.end_date || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateSearch;

    const [listAdvertiser, setListAdvertiser] = useState<IAdvertiser[]>([]);
    const [advertiserSearch, setAdvertiserSearch] = useState<Option | null>(null);

    useEffect(() => {
        dispatch(onGetReportAdvertiserAgency(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        setQuery({
            ...query,
            page: 1,
            start_date: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            end_date: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            // advertising_id: advertiserSearch?.value || '',
            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            // advertising_id: '',
            start_date: `${moment(new Date(new Date().setDate(new Date().getDate() - 30))).format("Y-MM-DD")}`,
            end_date: `${moment(new Date()).format("Y-MM-DD")}`,
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setDateSearch([moment(queryNew?.start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.end_date || "", 'Y-MM-DD').toDate()]);
        // setAdvertiserSearch({ label: t('All Advertiser'), value: '' });
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Date'),
                accessor: "date",
                filterable: true,
                sortable: true,
                thWidth: 150,
                thClass: 'align-middle text-start',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const arrDate = String(cell?.value || '').split(' ');
                    const num = moment(cell?.value).days(); // 0 is Sunday , 1 is Monday, ... , 6 is Saturday
                    const classText = num === 0 ? 'text-danger row-bg-danger' : (num === 6 ? 'text-primary row-bg-primary' : '');
                    const textNote = t(`STANDS_${ARR_INDEX_TEXT_DAY_OF_WEEK[num]}`);
                    return (
                        !!item?.text_row ? <strong>{item?.text_row} </strong> :
                            <div className="text-start" style={{ minWidth: '120px' }}>
                                <span className={classText}>{moment(arrDate?.[0] || '', 'Y-MM-DD').format("MM-DD")} ({textNote})</span>
                            </div>

                    )
                },
            },
            // {
            //     Header: t('Advertiser Agency'),
            //     accessor: "advertiser_agency",
            //     filterable: false,
            //     sortable: false,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="d-flex flex-column align-items-start" style={{ minWidth: '120px' }}>
            //                     {item?.text_row ? <strong>{item?.text_row}</strong> : <div>
            //                         <CopyWrapper contentCopy={item?.advertiser_agency?.name} >
            //                             {item?.advertiser_agency?.name}
            //                         </CopyWrapper>
            //                     </div>}
            //                 </div>
            //             </>
            //         )
            //     },
            // },
            {
                Header: t('Impression'),
                accessor: "impression",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Click'),
                accessor: "click",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('CTR'),
                accessor: "ctr",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                        </>
                    )
                },
            },
            {
                Header: '',
                accessor: "_",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 100,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end" style={{ minWidth: '100px' }}></div>
                        </>
                    )
                },
            },
            // {
            //     Header: t('Expense'),
            //     accessor: "expense",
            //     filterable: false,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(item?.expense || 0)}</div>
            //             </>
            //         )
            //     },
            // },
            // {
            //     Header: t('Impression'),
            //     accessor: "impression",
            //     filterable: false,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
            //             </>
            //         )
            //     },
            // },
            // {
            //     Header: t('Advertiser Revenue'),
            //     accessor: "advertiser_revenue",
            //     filterable: false,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
            //             </>
            //         )
            //     },
            // },
            // {
            //     Header: t('Agency Revenue'),
            //     accessor: "agency_revenue",
            //     filterable: false,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
            //             </>
            //         )
            //     },
            // },
            // {
            //     Header: t('Total Revenue'),
            //     accessor: "total_revenue",
            //     filterable: false,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="text-end pe-3" style={{ minWidth: '100px' }}>{formatNumberWithCommas(cell?.value)}</div>
            //             </>
            //         )
            //     },
            // },
        ],
        [i18n?.language, userPermissions]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                const [res]: any = await Promise.all([getAllAdvertisers()]);
                const list = ([{ name: t('All Advertiser'), id: '', } as any]).concat(res?.data || []);
                const advertiser = list?.find((item) => String(item?.id || '') === String(query?.advertiser_id || '')) || null;
                setListAdvertiser((_prev) => list);
                setAdvertiserSearch((_prev) => ({
                    label: advertiser?.name || t('All Advertiser'),
                    value: advertiser?.id || '',
                }));
            } catch (error: any) {
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    useEffect(() => {
        if (!advertiserSearch?.value) {
            setAdvertiserSearch((prev) => ({ label: t('All Advertiser'), value: '' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    useEffect(() => {
        document.title = `${t("Advertiser Agency Report")} | AdsHub`;
        document.body.classList.remove("vertical-sidebar-enable");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);


    const categories = useMemo(() => {
        return advertiserAgencies?.list?.map((item: any) => item?.date).reverse() || [];
    }, [advertiserAgencies?.list, i18n?.language]);

    const series = useMemo(() => {
        const obChart = advertiserAgencies?.list?.reduce((ob: any, item: any) => {
            ob.impression?.push(Number((item?.impression || '').replace(/,/g, '')));
            ob.click?.push(Number((item?.click || '').replace(/,/g, '')));
            return ob;
        }, { impression: [], click: [] }) || [];
        return (
            [
                {
                    name: t('Impression'),
                    type: 'line',
                    data: obChart?.impression?.reverse(),
                },
                {
                    name: t('Click'),
                    type: 'column',
                    data: obChart?.click?.reverse(),
                },

            ]
        )
    }, [advertiserAgencies?.list, i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Advertiser Agency Report')} pageTitle={t('Advertiser Agency Report')} />
                    {isHavePermissionRole(ROLES_FOR_APP.ADVERTISER_AGENCY_REPORT, userPermissions) && (
                        <Row>
                            <Col sm={12} md={12}>
                                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                    <div className="ribbon round-shape" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem', top: '11px' }}>{t('This data is analyzed per 10 mins')}</div>
                                    <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                        <Row className="g-4 align-items-center">
                                            <div className="col-sm">
                                                <div>
                                                    <h5 className="card-title mb-0">
                                                        <span className="me-2">{t('Total')}:</span>
                                                        <CountUp
                                                            start={0}
                                                            end={advertiserAgencies?.pagination?.total || 0}
                                                            duration={1}
                                                            className="text-primary"
                                                        />
                                                    </h5>
                                                </div>
                                            </div>
                                        </Row>
                                    </CardHeader>
                                    <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                                        <Row className="g-4 align-items-center mt-0">
                                            {/* <Col sm={6} md={6} xl={2} xxl={2} className='mt-3 mt-sm-3 mt-md-2'>
                                                <LabelWrapper label={t('Advertiser')} isShow={true}>
                                                    <DropdownAdvertiser
                                                        name="advertising"
                                                        isMulti={false}
                                                        dataList={listAdvertiser}
                                                        initialValue={advertiserSearch}
                                                        placeholder={t('Advertiser')}
                                                        onChangeSelect={(event: any) => {
                                                            setAdvertiserSearch((_prev) => event);
                                                        }}
                                                    />
                                                </LabelWrapper>
                                            </Col> */}
                                            <Col sm={6} md={3} xl={3} xxl={3} className='date-picker-wrapper-custom mt-3 mt-md-2'>
                                                <LabelWrapper label={t('Date')} isShow={!!startDate || !!endDate}>
                                                    <DatePickerCustom
                                                        placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                        startDate={startDate || null}
                                                        endDate={endDate || null}
                                                        onChangePicker={handleChangePicker}
                                                        showOptions={[
                                                            'today', 'yesterday', 'two_day_ago',
                                                            'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                            'this_week', 'last_week',
                                                            'this_month', 'last_month',
                                                        ]}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={12} md={6} xl={6} xxl={6} className="hstack gap-1 justify-content-center justify-content-md-between mt-3 mt-md-2">
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary me-2 "
                                                        onClick={searchData}
                                                        disabled={isAdvertiserAgencyReportLoading}
                                                    >
                                                        <i className="ri-search-line align-bottom me-1"></i>{" "}
                                                        {t('Button Search')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary fs-14"
                                                        onClick={resetData}
                                                    >
                                                        <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                        {t('Button Reset')}
                                                    </button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    <ChartLine
                                        className="my-3"
                                        titles={[t('Impression'), t('Click')]}
                                        categories={categories}
                                        series={series}
                                    />
                                    <CardBody className="pt-3">
                                        <TableContainer
                                            className="custom-header-css"
                                            divClass="table-responsive table-card"
                                            tableClass="align-middle table-bordered-dashed table-have-first-row-is-total"
                                            theadClass="table-light text-muted"
                                            columns={columns}
                                            data={advertiserAgencies?.list?.length ? (
                                                [
                                                    {
                                                        ...advertiserAgencies?.total,
                                                        text_row: t('Sum')
                                                    },
                                                    ...advertiserAgencies?.list]
                                            ) : []}
                                            customPageSize={query.limit}
                                            customPageIndex={query.page - 1}
                                            totalRecords={advertiserAgencies?.pagination?.total}
                                            customPageCount={Math.ceil(Number(advertiserAgencies?.pagination?.total) / Number(advertiserAgencies?.pagination?.limit))}
                                            handleChangePage={handleChangePage}
                                            manualSorting={true}
                                            sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                            handleChangeSorting={handleChangeSorting}
                                            isLoading={isAdvertiserAgencyReportLoading}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default AdvertiserAgencyReport;
